import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  doc,
  getDoc,
} from "firebase/firestore";

import { db } from "../../firebaseConfig";
import { backdropDismiss } from "../../utils/backdropDismiss";
import {
  fetchSessionLogsForStudent,
  toDateMaybe,
} from "../tutor/session_logs/sessionLogApi";
import { fetchAllStudents } from "../admin/scheduling/schedulingApi";
import {
  fetchReportsForStudent,
  renameReport,
  deleteReport,
  REPORTS_COL,
} from "./progressReportApi";
import {
  toDateKey,
  formatDateDisplay,
  formatDateRange,
  toMillis,
  formatTime,
  sortByDateDesc,
  hwBadgeClass,
  buildReportTitle,
  createProgressReport,
} from "./progressReportLogic";
import SearchableStudentDropdown from "../admin/dsat/reports/SearchableStudentDropdown";
import { TextShimmer } from "../loading-ui/text-shimmer";
import { calcAvgRating, StarRating } from "./sessionRating";

import "./ProgressReportWorkspace.css";

// ─── Pagination ─────────────────────────────────────────────────────────────────

export function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;

  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= 1) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  return (
    <div className="prw-pagination">
      <button className="prw-pageBtn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {pages.map((p, idx) =>
        p === "…" ? (
          // eslint-disable-next-line react/no-array-index-key
          <span key={`e-${idx}`} className="prw-pageInfo">…</span>
        ) : (
          <button
            key={p}
            className={`prw-pageBtn${p === page ? " prw-pageBtn--active" : ""}`}
            onClick={() => onPage(p)}
          >
            {p}
          </button>
        )
      )}
      <button className="prw-pageBtn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
    </div>
  );
}


// ─── Context menu ───────────────────────────────────────────────────────────────

function ContextMenu({ x, y, onClose, items }) {
  const menuRef = useRef(null);

  useEffect(() => {
    function handleDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    }
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", handleDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  // Adjust so menu stays on screen
  const style = { position: "fixed", top: y, left: x, zIndex: 9999 };

  return (
    <div className="prw-ctxMenu" style={style} ref={menuRef} onMouseDown={(e) => e.stopPropagation()}>
      {items.map((item, idx) =>
        item.divider ? (
          // eslint-disable-next-line react/no-array-index-key
          <div key={`div-${idx}`} className="prw-ctxDivider" />
        ) : (
          <button
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            type="button"
            className={`prw-ctxItem${item.danger ? " prw-ctxItem--danger" : ""}`}
            onClick={() => { onClose(); item.onClick(); }}
          >
            {item.icon && <span className="prw-ctxIcon">{item.icon}</span>}
            {item.label}
          </button>
        )
      )}
    </div>
  );
}

// ─── Rename modal ───────────────────────────────────────────────────────────────

function RenameModal({ report, onSave, onClose }) {
  const [value, setValue] = useState(
    report?.customName || buildReportTitle(report)
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await renameReport(report.id || report.reportId, trimmed);
      onSave(trimmed);
    } catch (err) {
      console.error("Rename failed", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="prw-modalOverlay" {...backdropDismiss(onClose)}>
      <div className="prw-modalCard prw-modalCard--sm" onClick={(e) => e.stopPropagation()}>
        <div className="prw-modalHeader">
          <div>
            <div className="prw-modalTitle">Rename Report</div>
            <div className="prw-modalSub">Choose a custom name for this report.</div>
          </div>
          <button type="button" className="prw-modalClose" onClick={onClose}>×</button>
        </div>
        <div className="prw-modalBody">
          <input
            className="prw-input"
            style={{ width: "100%", boxSizing: "border-box" }}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") onClose(); }}
            autoFocus
          />
        </div>
        <div className="prw-modalFooter">
          <button type="button" className="prw-outlineBtn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="prw-generateBtn"
            style={{ height: 36, padding: "0 16px", fontSize: 13 }}
            onClick={handleSave}
            disabled={saving || !value.trim()}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete confirm modal ───────────────────────────────────────────────────────

function DeleteConfirmModal({ report, onConfirm, onClose }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteReport(report.id || report.reportId);
      onConfirm();
    } catch (err) {
      console.error("Delete failed", err);
      setDeleting(false);
    }
  };

  return (
    <div className="prw-modalOverlay" {...backdropDismiss(onClose)}>
      <div className="prw-modalCard prw-modalCard--sm" onClick={(e) => e.stopPropagation()}>
        <div className="prw-modalHeader">
          <div>
            <div className="prw-modalTitle">Delete Report</div>
            <div className="prw-modalSub">This action cannot be undone.</div>
          </div>
          <button type="button" className="prw-modalClose" onClick={onClose}>×</button>
        </div>
        <div className="prw-modalBody">
          <p style={{ margin: 0, fontSize: 14, color: "#334155", lineHeight: 1.6 }}>
            Are you sure you want to permanently delete{" "}
            <strong>"{buildReportTitle(report)}"</strong>?
          </p>
        </div>
        <div className="prw-modalFooter">
          <button type="button" className="prw-outlineBtn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="prw-generateBtn"
            style={{ height: 36, padding: "0 16px", fontSize: 13, background: "#dc2626", borderColor: "#dc2626" }}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Details modal ──────────────────────────────────────────────────────────────

function DetailsModal({ report, onClose }) {
  const generatedAt = toDateMaybe(report?.generatedAt);
  const generatedAtStr = generatedAt
    ? generatedAt.toLocaleString([], { dateStyle: "long", timeStyle: "short" })
    : "—";
  const [generatedByName, setGeneratedByName] = useState("Loading...");

  useEffect(() => {
    if (!report?.generatedByUid) {
      setGeneratedByName("—");
      return;
    }
    
    async function fetchUserName() {
      try {
        const userRef = doc(db, "users", report.generatedByUid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists() && userSnap.data().name) {
          setGeneratedByName(userSnap.data().name);
        } else {
          setGeneratedByName("Unknown User");
        }
      } catch (err) {
        console.error("Failed to fetch user name:", err);
        setGeneratedByName("Error loading name");
      }
    }
    
    fetchUserName();
  }, [report?.generatedByUid]);

  const rows = [
    { label: "Report ID", value: report?.id || report?.reportId || "—" },
    { label: "Student", value: report?.studentName || "—" },
    { label: "Date Range", value: formatDateRange(report?.startDate, report?.endDate) },
    { label: "Sessions Included", value: report?.sessionCount ?? "—" },
    { label: "Last Session Date", value: formatDateDisplay(report?.lastSessionDateKey) },
    { label: "Total Hours", value: report?.metrics?.totalHours ? `${report.metrics.totalHours.toFixed(1)} hrs` : "—" },
    { label: "Generated At", value: generatedAtStr },
    { label: "Generated By", value: report?.generatedByUid ? `${generatedByName} (${report.generatedByUid})` : "—" },
    { label: "AI Provider", value: report?.ai?.provider || "—" },
    { label: "Risk Level", value: report?.ai?.riskLevel || report?.metrics?.riskLevel || "—" },
    { label: "Tutors", value: (report?.tutors || []).join(", ") || "—" },
    { label: "Subjects", value: (report?.subjects || []).join(", ") || "—" },
  ];

  return (
    <div className="prw-modalOverlay" {...backdropDismiss(onClose)}>
      <div className="prw-modalCard" onClick={(e) => e.stopPropagation()}>
        <div className="prw-modalHeader">
          <div>
            <div className="prw-modalTitle">Report Details</div>
            <div className="prw-modalSub">{buildReportTitle(report)}</div>
          </div>
          <button type="button" className="prw-modalClose" onClick={onClose}>×</button>
        </div>
        <div className="prw-modalBody">
          <dl className="prw-detailList">
            {rows.map(({ label, value }) => (
              <div key={label} className="prw-detailRow">
                <dt className="prw-detailLabel">{label}</dt>
                <dd className="prw-detailValue">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="prw-modalFooter">
          <button type="button" className="prw-outlineBtn" onClick={onClose}>Close</button>
          <button
            type="button"
            className="prw-generateBtn"
            style={{ height: 36, padding: "0 16px", fontSize: 13 }}
            onClick={() => window.open(`/progress-report/${report.id || report.reportId}`, "_blank")}
          >
            Open Report ↗
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Report row ─────────────────────────────────────────────────────────────────

function ReportRow({ report, isGenerating = false, showStudentName = false, onRenamed, onDeleted }) {
  const [ctxMenu, setCtxMenu] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const openReport = () => {
    if (isGenerating || !report?.id) return;
    window.open(`/progress-report/${report.id}`, "_blank");
  };

  const handleContextMenu = (e) => {
    if (isGenerating) return;
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY });
  };

  if (isGenerating) {
    return (
      <tr className="prw-shimmerRow">
        <td colSpan={7}>
          <div className="prw-generatingLabel">
            <div className="prw-spinner" />
            <TextShimmer className="text-sm font-medium">Generating report…</TextShimmer>
          </div>
        </td>
      </tr>
    );
  }

  const title = buildReportTitle(report);
  const generatedAt = report?.generatedAt
    ? formatDateDisplay(toDateMaybe(report.generatedAt))
    : "—";

  return (
    <>
      <tr
        className="prw-clickableRow"
        onClick={openReport}
        onContextMenu={handleContextMenu}
      >
        <td style={{ fontWeight: 600, color: "#0f172a" }}>{title}</td>
        <td>{generatedAt}</td>
        <td style={{ color: "#64748b", fontSize: 12 }}>
          {formatDateRange(report.startDate, report.endDate)}
        </td>
        <td>{report.sessionCount ?? "—"}</td>
        <td style={{ color: "#64748b", fontSize: 12 }}>
          {formatDateDisplay(report.lastSessionDateKey)}
        </td>
        <td>
          <span className="prw-badge prw-badge--complete">Complete</span>
        </td>
        <td style={{ textAlign: "right" }}>
          <span className="prw-ctxHint">⋮</span>
        </td>
      </tr>

      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
          items={[
            {
              label: "View Details",
              icon: "ℹ",
              onClick: () => setDetailsOpen(true),
            },
            { divider: true },
            {
              label: "Rename Report",
              icon: "✏",
              onClick: () => setRenameOpen(true),
            },
            {
              label: "Delete Report",
              icon: "🗑",
              danger: true,
              onClick: () => setDeleteOpen(true),
            },
          ]}
        />
      )}

      {renameOpen && (
        <RenameModal
          report={report}
          onSave={(newName) => {
            setRenameOpen(false);
            onRenamed?.(report.id || report.reportId, newName);
          }}
          onClose={() => setRenameOpen(false)}
        />
      )}

      {deleteOpen && (
        <DeleteConfirmModal
          report={report}
          onConfirm={() => {
            setDeleteOpen(false);
            onDeleted?.(report.id || report.reportId);
          }}
          onClose={() => setDeleteOpen(false)}
        />
      )}

      {detailsOpen && (
        <DetailsModal
          report={report}
          onClose={() => setDetailsOpen(false)}
        />
      )}
    </>
  );
}

// ─── Session row ─────────────────────────────────────────────────────────────────

function SessionRow({ log }) {
  const handleClick = () => {
    if (!log?.id) return;
    localStorage.setItem("sessionLog_" + log.id, JSON.stringify(log));
    localStorage.setItem("tutorSession_" + log.id, JSON.stringify(log));
    window.open(`/session-detail?id=${log.id}`, "_blank");
  };

  const date = formatDateDisplay(log?.sessionStart || log?.sessionDateKey);
  const time = log?.sessionStart
    ? `${formatTime(log.sessionStart)} – ${formatTime(log.sessionEnd)}`
    : "—";
  const hwStatus = String(log?.homeworkStatus || "—");
  const avgRating = calcAvgRating(log);

  return (
    <tr className="prw-clickableRow" onClick={handleClick}>
      <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{date}</td>
      <td style={{ whiteSpace: "nowrap", fontSize: 12, color: "#64748b" }}>{time}</td>
      <td>{String(log?.tutorName || "—")}</td>
      <td>{String(log?.subject || "—")}</td>
      <td>
        <span className={`prw-badge ${hwBadgeClass(hwStatus)}`}>{hwStatus}</span>
      </td>
      <td>
        <StarRating value={avgRating} uid={log?.id} />
      </td>
      <td style={{ fontSize: 12, color: "#64748b" }}>
        {String(log?.studentFlag || "—")}
      </td>
    </tr>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────────

const REPORTS_PER_PAGE = 5;
const SESSIONS_PER_PAGE = 20;

export default function ProgressReportWorkspace() {
  const [allStudents, setAllStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);

  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [studentLogs, setStudentLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [recentReports, setRecentReports] = useState([]);

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");

  const [reportsPage, setReportsPage] = useState(1);
  const [sessionsPage, setSessionsPage] = useState(1);

  // ── Load recent reports (across all students) for default table
  useEffect(() => {
    let alive = true;
    setReportsLoading(true);
    getDocs(query(collection(db, REPORTS_COL), orderBy("generatedAt", "desc")))
      .then((snap) => {
        if (!alive) return;
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRecentReports(rows);
        if (!selectedStudentId) {
          setReports(rows);
          setReportsPage(1);
        }
      })
      .catch((err) => {
        console.error("Failed to load recent progress reports", err);
        if (alive && !selectedStudentId) setReports([]);
      })
      .finally(() => { if (alive) setReportsLoading(false); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sort students: those with recent reports first
  const latestReportByStudentId = useMemo(() => {
    const map = new Map();
    recentReports.forEach((r) => {
      const sid = String(r?.studentId || "");
      if (sid && !map.has(sid)) map.set(sid, r);
    });
    return map;
  }, [recentReports]);

  const sortedStudents = useMemo(() => {
    return [...allStudents]
      .map((s) => {
        const lr = latestReportByStudentId.get(s.uid) || null;
        return {
          ...s,
          lastDateLabel: lr?.generatedAt ? formatDateDisplay(lr.generatedAt) : "",
          lastGeneratedMs: toMillis(lr?.generatedAt),
        };
      })
      .sort((a, b) => {
        if (a.lastGeneratedMs || b.lastGeneratedMs) return b.lastGeneratedMs - a.lastGeneratedMs;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
  }, [allStudents, latestReportByStudentId]);

  // ── Load students list
  useEffect(() => {
    let alive = true;
    setStudentsLoading(true);
    fetchAllStudents()
      .then((rows) => {
        if (!alive) return;
        setAllStudents(
          rows.filter((r) => r?.name).map((r) => ({
            uid: r.id,
            name: String(r.name).trim(),
            email: String(r.email || ""),
          }))
        );
      })
      .catch((err) => { console.error("Failed to load students", err); })
      .finally(() => { if (alive) setStudentsLoading(false); });
    return () => { alive = false; };
  }, []);

  // ── Load sessions + per-student reports when student selected
  useEffect(() => {
    if (!selectedStudentId) {
      setStudentLogs([]);
      setStartDate("");
      setEndDate("");
      setReports(recentReports);
      setReportsPage(1);
      setGenerateError("");
      return;
    }

    const student = sortedStudents.find((s) => s.uid === selectedStudentId);
    if (!student) return;

    let alive = true;
    setLogsLoading(true);
    setReportsLoading(true);
    setStudentLogs([]);
    setStartDate("");
    setEndDate("");

    Promise.all([
      fetchSessionLogsForStudent(student.name, { maxRows: 4000 }),
      fetchReportsForStudent(selectedStudentId),
    ])
      .then(([logs, rpts]) => {
        if (!alive) return;
        const sorted = sortByDateDesc(logs);
        setStudentLogs(sorted);
        if (sorted.length) {
          const oldest = toDateMaybe(sorted[sorted.length - 1]?.sessionStart);
          if (oldest) setStartDate(toDateKey(oldest));
          setEndDate(toDateKey(new Date()));
        }
        setReports(rpts);
      })
      .catch((err) => {
        console.error("Failed to load student data", err);
        if (alive) { setStudentLogs([]); setReports([]); }
      })
      .finally(() => {
        if (alive) { setLogsLoading(false); setReportsLoading(false); }
      });

    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudentId]);

  // ── Filtered sessions in selected date range
  const filteredLogs = useMemo(() => {
    if (!startDate && !endDate) return studentLogs;
    const startMs = startDate ? new Date(`${startDate}T00:00:00`).getTime() : null;
    const endMs = endDate ? new Date(`${endDate}T23:59:59`).getTime() : null;
    return sortByDateDesc(
      studentLogs.filter((log) => {
        const ms = toDateMaybe(log?.sessionStart)?.getTime();
        if (!ms) return false;
        if (startMs !== null && ms < startMs) return false;
        if (endMs !== null && ms > endMs) return false;
        return true;
      })
    );
  }, [studentLogs, startDate, endDate]);

  const lastSessionDateKey = useMemo(
    () => (filteredLogs.length ? toDateKey(filteredLogs[0]?.sessionStart) || "" : ""),
    [filteredLogs]
  );

  // ── Generate report
  const handleGenerate = useCallback(async () => {
    if (!selectedStudentId || !filteredLogs.length) {
      setGenerateError("Please select a student and ensure there are sessions in the selected date range.");
      return;
    }
    setGenerateError("");

    // Cache hit: same student + dates + same last session date
    const cached = reports.find(
      (r) =>
        r.studentId === selectedStudentId &&
        r.startDate === startDate &&
        r.endDate === endDate &&
        r.status === "complete" &&
        r.lastSessionDateKey === lastSessionDateKey
    );
    if (cached) {
      window.open(`/progress-report/${cached.id}`, "_blank");
      return;
    }

    setGenerating(true);
    const student = sortedStudents.find((s) => s.uid === selectedStudentId);
    const studentName = student?.name || "";

    try {
      const reportDoc = await createProgressReport({
        studentId: selectedStudentId,
        studentName,
        filteredLogs,
        startDate,
        endDate,
      });
      const reportId = reportDoc.reportId;
      setReports((prev) => [reportDoc, ...prev]);
      setRecentReports((prev) => [reportDoc, ...prev.filter((r) => (r.id || r.reportId) !== reportId)]);
      setReportsPage(1);
      window.open(`/progress-report/${reportId}`, "_blank");
    } catch (err) {
      console.error("Failed to generate report", err);
      setGenerateError("Failed to generate report. Please try again.");
    } finally {
      setGenerating(false);
    }
  }, [selectedStudentId, filteredLogs, reports, startDate, endDate, lastSessionDateKey, sortedStudents]);

  // ── Rename handler
  const handleRenamed = useCallback((id, newName) => {
    const patchReport = (r) => (r.id === id || r.reportId === id) ? { ...r, customName: newName } : r;
    setReports((prev) => prev.map(patchReport));
    setRecentReports((prev) => prev.map(patchReport));
  }, []);

  // ── Delete handler
  const handleDeleted = useCallback((id) => {
    const filterOut = (arr) => arr.filter((r) => r.id !== id && r.reportId !== id);
    setReports(filterOut);
    setRecentReports(filterOut);
  }, []);

  // ── Pagination
  const totalReportPages = Math.max(1, Math.ceil(reports.length / REPORTS_PER_PAGE));
  const pagedReports = reports.slice((reportsPage - 1) * REPORTS_PER_PAGE, reportsPage * REPORTS_PER_PAGE);

  const totalSessionPages = Math.max(1, Math.ceil(filteredLogs.length / SESSIONS_PER_PAGE));
  const pagedSessions = filteredLogs.slice((sessionsPage - 1) * SESSIONS_PER_PAGE, sessionsPage * SESSIONS_PER_PAGE);

  const selectedStudent = sortedStudents.find((s) => s.uid === selectedStudentId) || null;
  const reportsSectionMeta = selectedStudent
    ? reportsLoading ? "Loading…" : `${reports.length} report${reports.length !== 1 ? "s" : ""} for ${selectedStudent.name}`
    : reportsLoading ? "Loading recent reports…" : `${reports.length} recent report${reports.length !== 1 ? "s" : ""} across all students`;

  return (
    <div className="prw-shell">
      <div className="prw-pageHeader">
        <h1>Session Progress Report</h1>
        <p>Generate comprehensive student progress reports for parents, tutors, and administrators.</p>
      </div>

      {/* ── Filter Card ── */}
      <div className="prw-card">
        <div className="prw-cardTitle">Generate Report</div>
        <div className="prw-filterRow">
          {/* Student dropdown with inline clear */}
          <div className="prw-fieldGroup">
            <div className="prw-label">Student</div>
            <div className="prw-dropdownWrap">
              <SearchableStudentDropdown
                students={sortedStudents}
                selectedId={selectedStudentId}
                onSelect={(uid) => {
                  setSelectedStudentId(uid);
                  setGenerateError("");
                  setReportsPage(1);
                  setSessionsPage(1);
                }}
                placeholder={studentsLoading ? "Loading students…" : "Select a student…"}
                disabled={studentsLoading}
              />
              {selectedStudentId && (
                <button
                  type="button"
                  className="prw-clearBtn"
                  title="Clear student selection"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedStudentId("");
                    setGenerateError("");
                    setReportsPage(1);
                    setSessionsPage(1);
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <div className="prw-fieldGroup">
            <div className="prw-label">Start Date</div>
            <input
              type="date"
              className="prw-input"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setSessionsPage(1); }}
            />
          </div>

          <div className="prw-fieldGroup">
            <div className="prw-label">End Date</div>
            <input
              type="date"
              className="prw-input"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setSessionsPage(1); }}
            />
          </div>

          <div className="prw-fieldGroup">
            <div className="prw-label">&nbsp;</div>
            <button
              type="button"
              className="prw-generateBtn"
              onClick={handleGenerate}
              disabled={generating || logsLoading || !selectedStudentId}
            >
              {generating ? (
                <>
                  <div className="prw-spinner" style={{ borderColor: "rgba(255,255,255,0.3)", borderTopColor: "#fff" }} />
                  Generating…
                </>
              ) : "Generate"}
            </button>
          </div>
        </div>

        {selectedStudent && (
          <div className="prw-hint">
            Sessions in range: <strong>{filteredLogs.length}</strong>
            {logsLoading && " (loading…)"}
          </div>
        )}
        {generateError && <div className="prw-errorBox">{generateError}</div>}
      </div>

      {/* ── Generated Reports ── */}
      <div className="prw-card">
        <div className="prw-sectionHeader">
          <div className="prw-sectionTitle">Generated Reports</div>
          <div className="prw-sectionMeta">{reportsSectionMeta}</div>
        </div>

        <div className="prw-tableWrap">
          <table className="prw-table">
            <thead>
              <tr>
                <th>Report</th>
                <th>Generated</th>
                <th>Date Range</th>
                <th>Sessions</th>
                <th>Last Session</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {generating && <ReportRow isGenerating />}

              {!generating && !reportsLoading && pagedReports.length === 0 && (
                <tr className="prw-emptyRow">
                  <td colSpan={7}>
                    {selectedStudentId
                      ? "No reports generated yet. Select a date range and click Generate."
                      : "No recently generated reports found yet."}
                  </td>
                </tr>
              )}

              {reportsLoading && !generating && (
                <tr className="prw-emptyRow"><td colSpan={7}>Loading reports…</td></tr>
              )}

              {!reportsLoading && pagedReports.map((r) => (
                <ReportRow
                  key={r.id || r.reportId}
                  report={r}
                  showStudentName={!selectedStudentId}
                  onRenamed={handleRenamed}
                  onDeleted={handleDeleted}
                />
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={reportsPage} totalPages={totalReportPages} onPage={setReportsPage} />
      </div>

      {/* ── Session Logs ── */}
      {selectedStudentId && (
        <div className="prw-card">
          <div className="prw-sectionHeader">
            <div className="prw-sectionTitle">Session Logs</div>
            <div className="prw-sectionMeta">
              {logsLoading
                ? "Loading sessions…"
                : `${filteredLogs.length} session${filteredLogs.length !== 1 ? "s" : ""} in range — click a row to open detail`}
            </div>
          </div>

          <div className="prw-tableWrap">
            <table className="prw-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Tutor</th>
                  <th>Subject</th>
                  <th>Homework</th>
                  <th>Avg Rating</th>
                  <th>Flag</th>
                </tr>
              </thead>
              <tbody>
                {logsLoading && (
                  <tr className="prw-emptyRow"><td colSpan={7}>Loading sessions…</td></tr>
                )}
                {!logsLoading && pagedSessions.length === 0 && (
                  <tr className="prw-emptyRow"><td colSpan={7}>No sessions found in the selected date range.</td></tr>
                )}
                {!logsLoading && pagedSessions.map((log) => (
                  <SessionRow key={log.id} log={log} />
                ))}
              </tbody>
            </table>
          </div>

          <Pagination page={sessionsPage} totalPages={totalSessionPages} onPage={setSessionsPage} />
        </div>
      )}
    </div>
  );
}
